import React, { useEffect, useLayoutEffect, useState } from "react";
import BasePage from "pages/common/basePage";
import Header from "pages/common/header";
import MainContent from "pages/common/mainContent";
import MoreOptionsEmail from "./moreOptionsEmail";
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import { Button } from "@mui/material";
import { push } from "react-router-redux";
import { GlobalApplicationState } from "globalApplicationState";
import { RouteComponentProps } from "react-router";
import { connect, ConnectedProps, useDispatch, useSelector } from "react-redux";
import EmailsListing from "./emailsListing";
import { messagingApi, reportsApi } from "api/instances";
import SenderEmailsDialog from "./senderEmailsDialog";
import { actions, Address } from "modules/newsletters";
import AssociatedNewslettersDialog from "modules/newsletters/management/components/associatedNewslettersDialog";
import { IdNamePair } from "modules/common/models";
import LoadingOverlay from "modules/common/components/loadingOverlay";
import { ApiRequest } from "api/network";
import { FileDownloader } from "utils/fileDownloader";
import SentEmailDialog from "./sentEmailDialog";
import { useSetSnackbarMessages } from "modules/common/hooks/useSetSnackbarMessages";
import { getEmailTemplates } from "../reducer";
import EmailTemplateModal from "./emailTemplateModal";
import LoadingCircle from "modules/common/components/loadingCircle";
import { useDisplayNav } from "modules/common/hooks/useDisplayNav";

interface IEmailsManagementPageProps { }

const EmailsManagementPage: React.FC<PropsWithRedux> = ({
    redirectTo,
    getAssociatedNewsletters,
    sentConfirmation,
    tenantId
}) => {
    const [showSenderEmailDialog, setShowSenderEmailDialog] = useState(false);
    const [isLoadingAssocNewsletters, setIsLoadingAssocNewsletters] = useState(false);
    const [showAssociatedNewslettersDialog, setShowAssociatedNewslettersDialog] = useState(false);
    const [associatedNewsletters, setAssociatedNewsletters] = useState<IdNamePair<string, string>[]>([]);
    const [associatedSender, setAssociatedSender] = useState<Address | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [showEmailTemplateModal, setShowEmailTemplateModal] = useState(false);
    useDisplayNav(true);
    const activeLcid = "en-us"; // we only support en-us for now

    const dispatch = useDispatch();
    const { templates, isFetching } = useSelector((state: GlobalApplicationState) => state.emails.emailTemplates);

    const { setSuccessMessage, setErrorMessage } = useSetSnackbarMessages();

    const onDownloadEmailActivity = async (emailId?: string) => {
        reportsApi.SendEmailActivityReportRequest(emailId)
            .then(() => setSuccessMessage("Your report is being generated and will be emailed to you when it’s ready."))
            .catch(() => setErrorMessage("Something went wrong. Please try again."));
    }

    const onCheckAssociatedNewsletters = async (senderDetails: Address) => {
        setIsLoadingAssocNewsletters(true);
        setShowSenderEmailDialog(false);
        setShowAssociatedNewslettersDialog(true);
        const associatedNewsletters = await getAssociatedNewsletters(senderDetails.fromEmailName, senderDetails.fromEmailAddress);
        setAssociatedNewsletters(associatedNewsletters);
        setAssociatedSender(senderDetails);
        setIsLoadingAssocNewsletters(false);
    }

    const onCloseAssociatedNewslettersDialog = () => {
        setShowAssociatedNewslettersDialog(false);
        setShowSenderEmailDialog(true);
    }

    useEffect(() => {
        dispatch(getEmailTemplates());
    }, []);

    const onDownloadReport = async (req: () => ApiRequest<any>, name: string) => {
        setIsLoading(true);
        
        req()
            .then(data => {
                new FileDownloader({ name: `${name}-${new Date().toISOString()}.csv` }).downloadBlob(data);
                setSuccessMessage("Successfully downloaded your report! Check your Downloads folder.");
            })
            .catch(() => setErrorMessage("Something went wrong. Please try again."))
            .finally(() => setIsLoading(false));
    }

    const redirectToEditEmail = () => 
        redirectTo("/" + tenantId + "/admin/emails/edit/new");
    
    return <BasePage fullWidth>
        <LoadingOverlay show={isLoading} />
        <Header
            title="Manage Emails"
            rightComponent={
                <React.Fragment>
                    <MoreOptionsEmail
                        onDownloadEmailActivityLog={() => onDownloadEmailActivity()}
                        onDownloadUserData={() => onDownloadReport(messagingApi.GetEmailUserCsv, "notification-email-user-data")}
                        onDownloadEmailOutbox={() => onDownloadReport(() => messagingApi.GetOutbox("email"), "notification-email-outbox-user-data")}
                        onClickManageSenderEmails={() => setShowSenderEmailDialog(true)}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={isFetching ? <LoadingCircle size="12px"/> : <SendOutlinedIcon />}
                        onClick={() => 
                            templates.length > 0 
                                ? setShowEmailTemplateModal(true) 
                                : redirectToEditEmail()
                        }
                        disabled={isFetching}
                    >
                        SEND NEW EMAIL
                    </Button>
                </React.Fragment>
            }
        />
        <MainContent>
            <div className="authoring-page">
                <EmailsListing onDownloadEmailActivity={onDownloadEmailActivity} activeLcid={activeLcid}/>
            </div>
            <SenderEmailsDialog
                show={showSenderEmailDialog}
                onClose={() => setShowSenderEmailDialog(false)}
                onErrorMessage={setErrorMessage}
                onCheckAssociatedNewsletters={onCheckAssociatedNewsletters}
            />
            <AssociatedNewslettersDialog
                show={showAssociatedNewslettersDialog} 
                onClose={onCloseAssociatedNewslettersDialog} 
                isLoading={isLoadingAssocNewsletters} 
                associatedNewsletters={associatedNewsletters}
                senderAddressCheck={associatedSender}
                backButtonLabel="Back to manage sender emails"
                backButtonProps={{ variant: "text" }}
            />
            <EmailTemplateModal
                show={showEmailTemplateModal}
                onClose={() => setShowEmailTemplateModal(false)}
                onSelectEmailTemplate={redirectToEditEmail}
            />
            { sentConfirmation.shouldShowDialog && <SentEmailDialog /> }
        </MainContent>
    </BasePage>;
};

const connector = connect(
    (state: GlobalApplicationState, ownProps: RouteComponentProps<IEmailsManagementPageProps>) =>
    ({
        ...ownProps,
        sentConfirmation: state.emails.sentConfirmation,
        tenantId: state.tenant.id,
    }),
    {
        redirectTo: push,
        getAssociatedNewsletters: actions.getAssociatedNewsletters,
    }
);
type PropsWithRedux = ConnectedProps<typeof connector>;

export default connector(EmailsManagementPage);
